import { FC, useMemo } from 'react';
import Image from 'next/image';
import Box from '@/components/UI/box/Box';
import Typography from '@/components/UI/typography/Typography';
import Divider from '@/components/UI/divider/Divider';
import Badges from '@/components/UI/badges/Badges';
import ShowMorePopUp from '@/components/houses/_reusable/houseInfo/ShowMorePopUp/ShowMorePopUp';
import HouseFeaturesCardItem from '@/components/houses/_reusable/houseInfo/houseFeatures/houseFeaturesCardItem/HouseFeaturesCardItem';
import styles from './HouseFeatures.module.scss';
import React from 'react';
import { ProductItemTypes } from '@/components/houses/_types/productItem.types';
const s3URL = process.env.s3URL || 'https://s3gw.shab.ir';
type importantFeaturesType = {
  icon_url: string;
  feature_label: string;
  feature_name: string;
  is_active: boolean;
  is_disabled?: boolean;
};
const categoryDic = {
  accommodation_services: {
    title: 'خدمات اقامتگاه',
    detail: ' (شامل صبحانه ، نهار و ...)'
  },
  amenities: {
    title: 'امکانات رفاهی',
    detail: '(شامل اتو ، مبلمان ، سشوار و ...)'
  },
  cctv_camera: {
    title: 'دوربین مدار بسته',
    detail: ''
  },
  cooling_system: {
    title: 'سیستم سرمایشی',
    detail: '(شامل پنکه، کولر گازی و ...)'
  },
  health_facilities: {
    title: 'پک بهداشتی یکبار مصرف',
    detail: '(شامل روتختی ، روبالشتی و ...)'
  },
  heating_system: {
    title: 'سیستم گرمایشی',
    detail: '(شامل بخاری، شوفاژ و ...)'
  },
  kitchen_facilities: {
    title: 'امکانات آشپزخانه',
    detail: ' (شامل وسایل سرو غذا و ...)'
  },
  safety_facilities: {
    title: 'امکانات ایمنی',
    detail: '(شامل نگهبان/ سرایدار و ...)'
  },
  spaces: {
    title: 'فضاهای اقامتگاه',
    detail: '(شامل آشپرخانه، پارکینگ، استخر و ...)'
  },
  main: {
    title: 'امکانات اصلی',
    detail: '(شامل آب، برق و ...)'
  },
  special_facilities: {
    title: 'امکانات ویژه',
    detail: '(شامل سیستم صوتی و ...)'
  }
};
const heatingFeatures = {
  icon_url: `${s3URL}/staging/img/house-feature-icons/v2/shofazh.svg`,
  feature_label: 'سیستم گرمایشی',
  feature_name: 'heating_system',
  is_active: false
};
const coolingFeatures = {
  icon_url: `${s3URL}/staging/img/house-feature-icons/v2/cooling_split.svg`,
  feature_label: 'سیستم سرمایشی',
  feature_name: 'cooling_system',
  is_active: false
};
const importantFeatures: importantFeaturesType[] = [{
  icon_url: `${s3URL}/staging/img/house-feature-icons/v2/parking.svg`,
  feature_label: 'پارکینگ',
  feature_name: 'parking',
  is_active: false
}, {
  icon_url: `${s3URL}/staging/img/house-feature-icons/v2/farangi_toilet.svg`,
  feature_label: 'سرویس بهداشتی فرنگی',
  feature_name: 'european_wc',
  is_active: false
}
// {
//   icon_url:
//     `${s3URL}/staging/img/house-feature-icons/v2/elevater.svg`,
//   feature_label: 'آسانسور',
//   feature_name: 'elevator',
//   is_active: false,
// },
];
type Props = {
  props: ProductItemTypes | undefined;
};
const HouseFeatures: FC<Props> = ({
  props
}) => {
  const featuresObject = {
    ...props?.features?.filled_categories
  };
  const flattedFeatures = Object?.values(featuresObject)?.flat();
  const poolItem = flattedFeatures?.find(item => item.feature_name === 'pool');
  const elevatorItem = flattedFeatures?.find(item => item.feature_name === 'elevator');
  const featuresPreview = useMemo(() => {
    const result: importantFeaturesType[] = [];
    if (!!featuresObject?.heating_system?.length) {
      result.push({
        icon_url: featuresObject.heating_system[0].icon_url,
        feature_label: featuresObject.heating_system[0].feature_label,
        feature_name: featuresObject.heating_system[0].feature_name,
        is_active: !!featuresObject.heating_system[0].is_active
      });
    } else {
      result.push(heatingFeatures);
    }
    if (!!featuresObject?.cooling_system?.length) {
      result.push({
        icon_url: featuresObject.cooling_system[0].icon_url,
        feature_label: featuresObject.cooling_system[0].feature_label,
        feature_name: featuresObject.cooling_system[0].feature_name,
        is_active: !!featuresObject.cooling_system[0].is_active
      });
    } else {
      result.push(coolingFeatures);
    }
    if (!!poolItem) {
      result.push({
        icon_url: poolItem.icon_url,
        feature_label: poolItem.feature_label,
        feature_name: poolItem.feature_name,
        is_active: !!poolItem.is_active,
        is_disabled: poolItem.meta_data.find(item => item.is_active === 0)
      });
    }
    if (!!elevatorItem) {
      result.push({
        icon_url: elevatorItem.icon_url,
        feature_label: elevatorItem.feature_label,
        feature_name: elevatorItem.feature_name,
        is_active: !!elevatorItem.is_active
      });
    }
    for (const index in importantFeatures) {
      const target = flattedFeatures.find(item => item.feature_name === importantFeatures[index].feature_name);
      result.push({
        icon_url: importantFeatures[index].icon_url,
        feature_label: importantFeatures[index].feature_label,
        feature_name: importantFeatures[index].feature_name,
        is_active: !!target
      });
    }
    const maxVisibleFeatures = 6;
    const remain = maxVisibleFeatures - result.length;
    for (let i = 0; i < remain; i++) {
      if (!flattedFeatures[i]) {
        break;
      }
      result.push({
        icon_url: flattedFeatures[i].icon_url,
        feature_label: flattedFeatures[i].feature_label,
        feature_name: flattedFeatures[i].feature_name,
        is_active: !!flattedFeatures[i].is_active
      });
    }
    return result;
  }, [featuresObject]);
  return <Box className={styles['house-features']} component={'section'} data-sentry-element="Box" data-sentry-component="HouseFeatures" data-sentry-source-file="HouseFeatures.tsx">
      <Box className={styles['container']} data-sentry-element="Box" data-sentry-source-file="HouseFeatures.tsx">
        <Typography variant="bodyL_Bold" color="neutral_800" component="h2" data-sentry-element="Typography" data-sentry-source-file="HouseFeatures.tsx">
          امکانات اقامتگاه
        </Typography>

        <Box className={styles['flat-content']} data-sentry-element="Box" data-sentry-source-file="HouseFeatures.tsx">
          <Box className={styles['flat-items']} data-sentry-element="Box" data-sentry-source-file="HouseFeatures.tsx">
            {featuresPreview.map((item, index) => <React.Fragment key={`FLAT_FEATURES_${index}`}>
                <Box className={styles['flat-item'] + ' ' + (item.is_active ? '' : styles['disabled'])}>
                  <Box className={styles['flat']}>
                    <Box className={styles['title']}>
                      <Image src={item.icon_url} alt={item.icon_url} width={24} height={24} />
                      <Typography variant="bodyM" color="neutral_800">
                        {item.feature_label}
                      </Typography>
                    </Box>
                    <Box className={styles['badge']}>
                      {item.feature_name === 'pool' && <Badges label={item.is_disabled ? 'غیرفعال' : 'فعال'} type={item.is_disabled ? 'error' : 'success'} />}
                    </Box>
                  </Box>
                </Box>
                {index % 2 === 0 && <Divider orientation="vertical" />}
              </React.Fragment>)}
          </Box>
        </Box>
      </Box>
      <ShowMorePopUp title="features" btnText={`مشاهده همه امکانات (${flattedFeatures.length}) مورد`} data-sentry-element="ShowMorePopUp" data-sentry-source-file="HouseFeatures.tsx">
        {Object?.entries(featuresObject)?.map(([key, item]) => <Box key={key}>
            <HouseFeaturesCardItem header={key} items={item} />
            <Divider orientation="horizontal" />
          </Box>)}
        {props?.features?.empty_categories.length as number > 0 && <Box className={styles['empty-section']}>
            <Typography variant="bodyL_Bold" className={styles['header']} component="h3">
              امکاناتی که در اقامتگاه فراهم نیست.
            </Typography>

            {props?.features?.empty_categories?.map((item, index) => <Box key={`EMPTY-${index}`} className={styles['empty-texts']}>
                <Typography variant="bodyL" color="secondary_main" className={styles['empty']}>
                  {categoryDic[item]?.title}
                </Typography>
                <Typography variant="bodyL" color="secondary_main">
                  {categoryDic[item]?.detail}
                </Typography>
              </Box>)}
          </Box>}
      </ShowMorePopUp>
      <Divider orientation="horizontal" data-sentry-element="Divider" data-sentry-source-file="HouseFeatures.tsx" />
    </Box>;
};
export default HouseFeatures;